/**
 * Material-UI styles hook for the KeywordsCheckbox component.
 *
 * @module components/sim/keywords/KeywordsCheckbox
 * @author Covintus
 * @author Snowfly
 * @version 1.0.0
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * A JSS-based object created with the `makeStyles` function, containing style definitions
 * for customizing the appearance of a checkbox component and its related elements.
 *
 * Properties:
 * - **formControl**: Styles for the form control container, setting display to flex
 *   and applying top and left margins.
 * - **checkbox**: Styles for the checkbox element, including negative right margin and
 *   right-alignment of its content.
 * - **divider**: Styles for a divider element, applying a right margin based on theme spacing.
 * - **textFont**: Styles for text elements, setting font size and a specific color value.
 * - **select**: Styles for select elements within the checkbox component. Includes nested
 *   styling for the `MuiNativeSelect-select` and `MuiNativeSelect-icon` elements.
 */
export const useCheckboxStyles = makeStyles((theme) => ({
    formControl: {
        display: 'flex',
        margin: theme.spacing(1, 0, 0, 1),
    },
    checkbox: {
        marginRight: -10,
        justifyContent: 'flex-end',
    },
    divider: {
        marginRight: theme.spacing(2),
    },
    textFont: {
        fontSize: 12,
        color: '#696867',
    },
    select: {
        '& .MuiNativeSelect-select': {
            fontSize: 12,
            color: '#646466',
            marginLeft: 20,
        },
        '& .MuiNativeSelect-icon': {
            left: '-6px',
        },
    },
}));

/**
 * A variable that contains a set of JSS styles created using the makeStyles function.
 * It defines custom styling for a tooltip and its arrow, primarily targeting color,
 * background color, width, and padding.
 *
 * Properties:
 * - `arrow`: Customizes the color of the arrow element in the tooltip.
 * - `tooltip`: Styles the tooltip container with a specific background color, width, and padding.
 *
 * This styling configuration is typically used to apply consistent design to tooltips
 * throughout an application.
 */
export const useStylesBootstrap = makeStyles(() => ({
    arrow: {
        color: '#1B1B1B',
    },
    tooltip: {
        backgroundColor: '#1B1B1B',
        width: '200px',
        padding: '8px',
    },
}));
